import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useStore } from "@/context/Store";
import { Container, useSeo } from "@/components/site/common";

export const LEGAL = {
  privacidade: { key: "privacy", title: "Política de Privacidade" },
  termos: { key: "terms", title: "Termos e Condições" },
  devolucoes: { key: "returns", title: "Política de Devoluções" },
};

export const fillLegal = (text, s) =>
  (text || "").replaceAll("{empresa}", s?.legal_name || "DUSA").replaceAll("{nif}", s?.nif || "").replaceAll("{morada}", s?.legal_address || "").replaceAll("{email}", s?.contact_email || "");

function toBlocks(text) {
  const blocks = [];
  text.split(/\n\s*\n/).forEach((chunk) => {
    let lines = chunk.split("\n").filter((l) => l.trim());
    if (lines[0]?.startsWith("## ")) { blocks.push({ type: "h", text: lines[0].slice(3) }); lines = lines.slice(1); }
    if (!lines.length) return;
    if (lines.every((l) => l.startsWith("- "))) blocks.push({ type: "ul", items: lines.map((l) => l.slice(2)) });
    else blocks.push({ type: "p", text: lines.join(" ") });
  });
  return blocks;
}

function Rich({ text }) {
  const blocks = useMemo(() => toBlocks(text), [text]);
  return blocks.map((b, i) => {
    if (b.type === "h") return <h2 key={i} className="mt-10 font-display text-2xl font-bold text-[var(--ink)]">{b.text}</h2>;
    if (b.type === "ul") return <ul key={i} className="mt-4 list-disc space-y-2 pl-5">{b.items.map((t, j) => <li key={j}>{t}</li>)}</ul>;
    return <p key={i} className="mt-4">{b.text}</p>;
  });
}

export function LegalPage() {
  const { slug } = useParams();
  const { settings } = useStore();
  const page = LEGAL[slug];
  const others = useMemo(() => Object.entries(LEGAL).filter(([k]) => k !== slug), [slug]);
  useSeo(page?.title, page ? `${page.title} da DUSA.` : null);
  if (!page) return <Container className="py-24 text-center"><p className="font-display text-3xl">Página não encontrada</p></Container>;
  return (
    <Container className="max-w-3xl py-16">
      <article data-testid={`legal-page-${slug}`}>
        <p className="eyebrow">Informação legal</p>
        <h1 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">{page.title}</h1>
        <div className="leading-relaxed text-[var(--muted-ink)]"><Rich text={fillLegal(settings?.[page.key], settings)} /></div>
        <div className="mt-12 flex flex-wrap gap-3 border-t border-[var(--line)] pt-6 text-sm">
          {others.map(([k, v]) => <Link key={k} to={`/legal/${k}`} className="btn-ghost !py-2">{v.title}</Link>)}
        </div>
      </article>
    </Container>
  );
}

export const TermsCheckbox = ({ checked, onChange, testid }) => (
  <label className="flex items-start gap-3 text-xs leading-relaxed text-[var(--muted-ink)]">
    <input type="checkbox" required data-testid={testid} checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--violet)]" />
    <span>Li e aceito os <Link to="/legal/termos" target="_blank" className="font-semibold text-[var(--violet)] underline">Termos e Condições</Link> e a <Link to="/legal/privacidade" target="_blank" className="font-semibold text-[var(--violet)] underline">Política de Privacidade</Link>.</span>
  </label>
);

const COOKIE_KEY = "dusa_cookie_notice_v1";

export function CookieBanner() {
  const [open, setOpen] = useState(() => !localStorage.getItem(COOKIE_KEY));
  if (!open) return null;
  const ok = () => { localStorage.setItem(COOKIE_KEY, new Date().toISOString()); setOpen(false); };
  return (
    <div className="fixed inset-x-3 bottom-24 z-50 mx-auto max-w-xl rounded-2xl border border-[var(--line)] bg-white/95 p-4 shadow-2xl backdrop-blur-xl sm:bottom-6 sm:left-6 sm:right-auto" data-testid="cookie-banner">
      <p className="text-sm text-[var(--muted-ink)]">Usamos apenas armazenamento essencial para guardar o teu carrinho e o funcionamento do site. Sem publicidade nem rastreamento. <Link to="/legal/privacidade" className="font-semibold text-[var(--violet)] underline">Saber mais</Link></p>
      <button data-testid="cookie-banner-accept" onClick={ok} className="btn-primary mt-3 !py-2">Entendi</button>
    </div>
  );
}

export function NotFound() {
  useSeo("Página não encontrada");
  return (
    <Container className="py-24 text-center">
      <div data-testid="not-found-page">
        <p className="font-display text-7xl font-bold text-gradient">404</p>
        <p className="mt-4 font-display text-3xl font-bold">Esta página não existe</p>
        <p className="mt-2 text-[var(--muted-ink)]">Talvez a ligação esteja errada ou o produto tenha saído da loja.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3"><Link to="/" className="btn-primary" data-testid="not-found-home-link">Voltar ao início</Link><Link to="/ideia" className="btn-ghost">Pedir orçamento</Link></div>
      </div>
    </Container>
  );
}
